import { Box, Card, CardBody, CardHeader, Heading, Image, Stack, Text } from '@chakra-ui/react';
import { FreightService } from '@pelicargo/types';
import { buildFlightPathString, Show } from '@pelicargo/ui';
import {
  getPrettyEarliestTenderOption,
  prettyAddress,
  prettyContact,
  prettyDate,
  prettyDockHours,
} from '@pelicargo/utils';
import { useMemo } from 'react';

import { CopyButton } from '../../components/CopyButton';
import { CopyLineItemsButton } from '../../components/CopyLineItemsButton';
import { InstructionsCard } from '../../components/InstructionCard';
import { LabeledValue } from '../../components/LabeledValue';
import { PaperworkCard } from '../../components/PaperworkCard';
import { QuoteVariantUI } from '../../components/QuoteVariantTabs/QuoteVariant';
import { RequestDetailsCard } from '../../components/RequestDetailsCard';
import { Timeline } from '../../components/Timeline';
import { Validity } from '../../components/Validity';
import { TrpcOutputs } from '../../config/trpc';
import { useFlightData } from '../../hooks/useFlightData';

type Props = {
  quote: TrpcOutputs['getFinalQuote']['quote'];
};

export const FCAFPDashboard = ({ quote }: Props) => {
  const { flightData } = useFlightData(quote?.flight_path);

  const bookedQuoteVariant = quote?.request?.booked_quote_variant;
  const inlandQuote = bookedQuoteVariant?.inland_service_quote_variant?.quote;
  const isFCA = inlandQuote?.freight_service === FreightService.FCA;
  const isEXW = inlandQuote?.freight_service === FreightService.EXW;
  const isInland = isFCA || isEXW;

  const stopCountLabel = useMemo(() => {
    const numStops = quote?.flight_path?.length - 1;

    if (numStops === 0) return 'Nonstop';
    if (numStops === 1) return '1 stop';
    return `${numStops} stops`;
  }, [quote?.flight_path?.length]);

  const earliestTenderOption = getPrettyEarliestTenderOption(quote?.earliest_tender);
  const minColumnWidth = '100px';

  return (
    <Stack direction={{ base: 'column', lg: 'row' }} spacing="6">
      <Stack w="full" spacing="6">
        <Show if={!!quote?.booking_instructions && !isInland}>
          <InstructionsCard instructions={quote?.booking_instructions} />
        </Show>
        <Card borderRadius="lg" role="group" w="full" h="fit-content">
          <CardBody>
            <Stack direction={{ base: 'column', lg: 'row' }} spacing="6">
              <Stack flex="1">
                <Heading variant="h5">Warehouse Drop-off</Heading>
                <LabeledValue
                  label="Drop-off Date"
                  value={prettyDate(inlandQuote?.dropoff_at, { timeZone: 'Etc/UTC' })}
                />
                <LabeledValue
                  label="Truck Company"
                  value={bookedQuoteVariant?.inland_service_quote_variant?.airline_ref}
                  defaultValue="-"
                />
                <LabeledValue label="Address" value={prettyAddress(inlandQuote?.delivery_address)} defaultValue="-" />
                <LabeledValue
                  label="Dock Hours"
                  value={prettyDockHours(inlandQuote?.from_dock_hours, inlandQuote?.to_dock_hours)}
                  defaultValue="-"
                />
                <LabeledValue
                  label="Warehouse Contact"
                  value={
                    prettyContact(inlandQuote?.dropoff_contact) ?? prettyContact(inlandQuote?.inland_dropoff_contact)
                  }
                  defaultValue="-"
                />
              </Stack>
              <Stack spacing="6" flex="1">
                <Heading variant="h5">Airline Drop-Off</Heading>

                <Stack>
                  <LabeledValue
                    label="Address"
                    value={prettyAddress(quote?.delivery_address)}
                    minColumnWidth={minColumnWidth}
                    defaultValue="-"
                  />
                  <LabeledValue
                    label="Cut Off Date"
                    value={prettyDate(quote?.cutoff_at, {
                      showTime: true,
                      timeZone: quote?.origin_airport?.time_zone,
                    })}
                    minColumnWidth={minColumnWidth}
                    defaultValue="-"
                  />
                  <LabeledValue
                    label="Earliest Tender"
                    value={earliestTenderOption}
                    defaultValue="-"
                    minColumnWidth={minColumnWidth}
                  />
                  <LabeledValue
                    label="Drop Off Notes"
                    value={quote?.dropoff_instructions}
                    defaultValue="-"
                    minColumnWidth={minColumnWidth}
                  />
                </Stack>
              </Stack>
            </Stack>
            {/*</SimpleForm>*/}
          </CardBody>
        </Card>
        <RequestDetailsCard request={quote?.request} quote={quote} showPaperwork={false} />
      </Stack>
      <Stack w="full" spacing="6" alignItems="flex-start">
        <PaperworkCard quote={quote} variant="fulfillment-partner" />
        <Card h="fit-content">
          <CardBody p="4">
            <Stack spacing={6}>
              <Stack direction="row" align="center" justify="space-between" spacing="4" role="group">
                <Heading variant="h5">Cost Summary</Heading>
                <CopyLineItemsButton quote={quote} quoteVariant={bookedQuoteVariant} />
              </Stack>
              <QuoteVariantUI
                quote={quote}
                quoteVariant={bookedQuoteVariant}
                showCopy={false}
                showTotal={false}
                showMarkupBreakdown
              />
              <Validity quote={quote} />
            </Stack>
          </CardBody>
        </Card>
        <Card h="fit-content" w="full">
          <CardHeader py="2">
            <Heading variant="h5">Airline & Flight Details</Heading>
          </CardHeader>
          <CardBody py="2">
            <Stack>
              <Stack direction="row" align="center" spacing="6">
                <Image src={quote?.provider?.logo_uri} height="48px" width="48px" alt={quote?.provider?.name} />
                <Heading variant="h6">
                  {quote?.origin_airport_id}{' '}
                  <Box as="span" color="text.placeholder">
                    →
                  </Box>{' '}
                  {quote?.destination_airport_id}
                </Heading>
                <Text textStyle="bodySmallRegular">{stopCountLabel}</Text>
                <CopyButton text={buildFlightPathString(quote?.flight_path)} marginLeft="auto" />
              </Stack>
              <Text color="text.subtle" textStyle="captionSmallRegular">
                {quote?.provider?.name}
              </Text>
              <Timeline data={flightData} />
            </Stack>
          </CardBody>
        </Card>
      </Stack>
    </Stack>
  );
};
